import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Award } from "lucide-react";

// ✅ Replace with your images
import cert1 from "../assets/certificates/cert1.jpg";
import cert2 from "../assets/certificates/cert2.jpg";
import cert3 from "../assets/certificates/cert3.jpg";
import cert4 from "../assets/certificates/cert4.jpg";
import cert5 from "../assets/certificates/cert5.jpg";
import cert6 from "../assets/certificates/cert6.jpg";
import cert7 from "../assets/certificates/cert7.jpg";
import cert8 from "../assets/certificates/cert8.jpg";
import cert9 from "../assets/certificates/cert9.jpg";

type Cert = {
  id: string;
  title: string;
  image: string;
};

function mod(n: number, m: number) {
  return ((n % m) + m) % m;
}

export default function CertificatesCarousel() {
  const certificates: Cert[] = useMemo(
    () => [
      { id: "c1", title: "GMP Certificate", image: cert1 },
      { id: "c2", title: "Halal Certificate", image: cert2 },
      { id: "c3", title: "ISO Certificate", image: cert3 },
        { id: "c4", title: "HACCP Certificate", image: cert4 },
        { id: "c5", title: "Organic Certification", image: cert5 },
        { id: "c6", title: "FDA Registration", image: cert6 },
        { id: "c7", title: "Kosher Certification", image: cert7 },
        { id: "c8", title: "Non-GMO Project", image: cert8 },
        { id: "c9", title: "Third-Party Lab Testing", image: cert9 },
    ],
    []
  );

  const [index, setIndex] = useState(0);

  const prev = () => setIndex((i) => mod(i - 1, certificates.length));
  const next = () => setIndex((i) => mod(i + 1, certificates.length));

  // keyboard arrows
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [certificates.length]);

  const leftIndex = mod(index - 1, certificates.length);
  const rightIndex = mod(index + 1, certificates.length);

  const center = certificates[index];
  const left = certificates[leftIndex];
  const right = certificates[rightIndex];

  return (
    <section className="relative overflow-hidden py-20 md:py-24 bg-[#fbfbfd]">
      {/* Elegant background */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(900px_circle_at_20%_10%,rgba(244,124,32,0.12),transparent_60%),radial-gradient(900px_circle_at_80%_20%,rgba(232,62,111,0.10),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_40%,rgba(0,0,0,0.04)_100%)]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white/70 px-4 py-2 backdrop-blur">
            <Award size={16} className="text-[#C62828]" />
            <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#2B2B2B]/75">
              Certifications
            </span>
          </div>

          <h2 className="mt-6 text-4xl md:text-5xl font-extrabold text-[#1B1B1B]">
            Our{" "}
            <span className="bg-gradient-to-r from-[#F47C20] to-[#E83E6F] bg-clip-text text-transparent">
              Certificates
            </span>
          </h2>

          <p className="mt-4 text-base md:text-lg text-[#2B2B2B]/75 leading-relaxed">
            Verified quality standards that support export-ready manufacturing across global markets.
          </p>
        </div>

        {/* Carousel */}
        <div className="mt-12 relative">
          {/* Side soft glow */}
          <div className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 h-72 w-72 rounded-full bg-[#F47C20]/10 blur-3xl" />
          <div className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 h-72 w-72 rounded-full bg-[#E83E6F]/10 blur-3xl" />

          {/* Nav buttons */}
          <button
            onClick={prev}
            className="absolute left-0 md:left-8 top-1/2 -translate-y-1/2 z-20 h-11 w-11 rounded-full
                       bg-white/90 border border-black/10 shadow-md hover:shadow-lg transition
                       flex items-center justify-center"
            aria-label="Previous certificate"
          >
            <ChevronLeft />
          </button>

          <button
            onClick={next}
            className="absolute right-0 md:right-8 top-1/2 -translate-y-1/2 z-20 h-11 w-11 rounded-full
                       bg-white/90 border border-black/10 shadow-md hover:shadow-lg transition
                       flex items-center justify-center"
            aria-label="Next certificate"
          >
            <ChevronRight />
          </button>

          {/* Slides container */}
          <div className="relative mx-auto max-w-5xl">
            <div className="relative h-[380px] md:h-[460px] flex items-center justify-center">
              {/* Left card (peek) */}
              <motion.div
                className="hidden md:block absolute left-0 w-[260px] lg:w-[300px]"
                initial={false}
                animate={{ x: -30, scale: 0.88, opacity: 0.55 }}
                transition={{ type: "spring", stiffness: 220, damping: 22 }}
              >
                <CertCard cert={left} variant="side" />
              </motion.div>

              {/* Right card (peek) */}
              <motion.div
                className="hidden md:block absolute right-0 w-[260px] lg:w-[300px]"
                initial={false}
                animate={{ x: 30, scale: 0.88, opacity: 0.55 }}
                transition={{ type: "spring", stiffness: 220, damping: 22 }}
              >
                <CertCard cert={right} variant="side" />
              </motion.div>

              {/* Center card (active) with swipe */}
              <div className="relative w-full flex items-center justify-center">
                <AnimatePresence mode="popLayout">
                  <motion.div
                    key={center.id}
                    drag="x"
                    dragConstraints={{ left: 0, right: 0 }}
                    dragElastic={0.12}
                    onDragEnd={(_, info) => {
                      const threshold = 90;
                      if (info.offset.x > threshold) prev();
                      if (info.offset.x < -threshold) next();
                    }}
                    initial={{ opacity: 0, scale: 0.92, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.92, y: 10 }}
                    transition={{ type: "spring", stiffness: 260, damping: 24 }}
                    className="w-[320px] sm:w-[380px] md:w-[460px] lg:w-[520px]"
                  >
                    <CertCard cert={center} variant="center" />
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* Dots */}
            <div className="mt-8 flex items-center justify-center gap-2">
              {certificates.map((c, i) => (
                <button
                  key={c.id}
                  onClick={() => setIndex(i)}
                  className={`h-2.5 rounded-full transition-all ${
                    i === index
                      ? "w-8 bg-gradient-to-r from-[#F47C20] to-[#E83E6F]"
                      : "w-2.5 bg-black/15 hover:bg-black/25"
                  }`}
                  aria-label={`Go to certificate ${i + 1}`}
                />
              ))}
            </div>

            {/* Caption */}
            <p className="mt-4 text-center text-sm text-[#2B2B2B]/70">
              {center.title}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function CertCard({
  cert,
  variant,
}: {
  cert: { title: string; image: string };
  variant: "center" | "side";
}) {
  return (
    <div
      className={`relative rounded-2xl overflow-hidden border border-black/10 bg-white shadow-md ${
        variant === "center" ? "shadow-[0_22px_70px_rgba(0,0,0,0.18)]" : ""
      }`}
    >
      {/* top accent line */}
      <div className="h-[4px] w-full bg-gradient-to-r from-[#F47C20] via-[#C9A227] to-[#E83E6F]" />

      {/* image */}
      <div className="relative">
        <img
          src={cert.image}
          alt={cert.title}
          className="w-full h-[320px] md:h-[400px] object-contain"
          draggable={false}
        />

        {/* soft overlay for premium depth */}
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(400px_circle_at_50%_15%,rgba(244,124,32,0.06),transparent_55%)]" />
      </div>
    </div>
  );
}